import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  asBoolean,
  categoryThumbnailUrl,
  deleteCategory,
  fetchCategories,
} from "../../api/categoryApi";
import ModuleSearchControls, {
  ModulePerPageMenu,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import ProductsNav from "./components/ProductsNav";
import { resolveApiError } from "../../utils/apiError";
import "./products.scss";

const SORT_OPTIONS = [
  { value: "sort_order", label: "Display order" },
  { value: "created_at", label: "Created date" },
  { value: "name", label: "Name" },
  { value: "slug", label: "Slug" },
  { value: "status", label: "Status" },
];

const ProductCategoriesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState("desc");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [error, setError] = useState("");
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchCategories({
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setCategories(result.items);
      setMeta(result.meta);
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortOrder, navigate]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!location.state?.notice) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return categories;
    return categories.filter((category) =>
      `${category.name} ${category.name_zh || ""} ${category.slug || ""}`.toLowerCase().includes(keyword)
    );
  }, [categories, query]);

  const resetControls = () => {
    setQuery("");
    setSortBy("sort_order");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  const removeCategory = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError("");
    try {
      await deleteCategory(pendingDelete.id);
      setNotice("Category deleted.");
      setPendingDelete(null);
      await loadCategories();
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="products-page">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && (
        <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>
      )}

      <div className="products-page__heading">
        <div>
          <h2>Product categories</h2>
          <p>Create, edit and organise the categories used in the product catalogue.</p>
        </div>
        <button
          className="ui-button ui-button--primary"
          type="button"
          onClick={() => navigate("/admin/products/categories/create")}
        >
          <Icon icon="ph:plus" height={14} /> New category
        </button>
      </div>

      <ProductsNav />

      <section className="products-page__card">
        <ModuleSearchControls value={query} onChange={setQuery} placeholder="Search category name or slug">
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={resetControls}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>

        <DataTable
          loading={loading}
          loadingMessage="Loading categories…"
          emptyMessage="No categories match your search."
          rows={filtered}
          columns={[
            {
              key: "name",
              header: "Category",
              render: (category) => {
                const thumb = categoryThumbnailUrl(category);
                return (
                  <div className="products-page__product">
                    {thumb ? <img src={thumb} alt="" /> : <span>{(category.name || "?").slice(0, 2).toUpperCase()}</span>}
                    <div>
                      <strong>{category.name}</strong>
                      <small>{category.name_zh || "No Chinese name"}</small>
                    </div>
                  </div>
                );
              },
            },
            {
              key: "slug",
              header: "Slug",
              render: (category) => <span className="products-page__category">{category.slug || "—"}</span>,
            },
            {
              key: "status",
              header: "Status",
              render: (category) => (
                <small className="state-label">{category.status === false ? "Inactive" : "Active"}</small>
              ),
            },
            {
              key: "show_in_navigation_menu",
              header: "Navigation",
              render: (category) => (
                <small className="state-label">
                  {asBoolean(category.show_in_navigation_menu, true) ? "Yes" : "No"}
                </small>
              ),
            },
            { key: "sort_order", header: "Display order", render: (category) => category.sort_order ?? 0 },
            {
              key: "actions",
              header: "Actions",
              render: (category) => (
                <DataTableActions>
                  <button type="button" onClick={() => navigate(`/admin/products/categories/${category.id}/edit`)}>
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => setPendingDelete(category)}>
                    Delete
                  </button>
                </DataTableActions>
              ),
            },
          ]}
        />
        <DataTableFooter meta={meta} page={page} loading={loading} onPageChange={setPage} noun="categories" />
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete category"
        message={
          pendingDelete
            ? `Delete “${pendingDelete.name}”? Products in this category will become uncategorised.`
            : ""
        }
        confirming={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={removeCategory}
      />
    </div>
  );
};

export default ProductCategoriesPage;
